import { Link } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';

import Badge from '../../components/base/Badge';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import { coverageOf, COVERAGE_BADGE } from '../../helpers/orderCoverage';
import { orderProducts, orderProductsSearchText } from '../../helpers/orderProducts';
import { clientBadgeColor } from '../../helpers/sourceBreakdown';
import OrderCablesDetail from '../sales-management/OrderCablesDetail';

// Helper: safe date
const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const sumLines = (order, fn) => {
  const lines = Array.isArray(order?.lines) ? order.lines : [];
  return lines.reduce((s, l) => s + Number(fn(l) || 0), 0);
};

const getJobsCount = order => {
  const lines = Array.isArray(order?.lines) ? order.lines : [];
  let c = 0;
  for (const l of lines) {
    const a = l?.allocation?.allocations;
    if (Array.isArray(a)) c += a.length;
  }
  return c;
};

const urgencyInfo = dueDateIso => {
  // No grey anywhere in this column. A missing or unreadable date is not
  // "nothing to see" — it is a date somebody has to go and find, which is
  // what `warning` says and grey does not.
  if (!dueDateIso) return { label: 'NO DATE', sub: 'not set', color: 'warning' };

  const due = new Date(dueDateIso);
  if (Number.isNaN(due.getTime()))
    return { label: 'BAD DATE', sub: String(dueDateIso), color: 'warning' };

  // normalize to date-only (local)
  const today = new Date();
  const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d1 = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  const diffDays = Math.round((d1 - d0) / (24 * 3600 * 1000)); // + future, - past

  if (diffDays < 0)
    return {
      label: `D+${Math.abs(diffDays)}`,
      sub: formatDate(dueDateIso),
      color: 'danger'
    };
  if (diffDays === 0)
    return { label: 'TODAY', sub: formatDate(dueDateIso), color: 'warning' };
  if (diffDays <= 3)
    return {
      label: `D-${diffDays}`,
      sub: formatDate(dueDateIso),
      color: 'warning'
    };
  if (diffDays <= 7)
    return {
      label: `D-${diffDays}`,
      sub: formatDate(dueDateIso),
      color: 'info'
    };

  // Comfortably far off. Still a real date, so it keeps a real colour —
  // `success` reads as "nothing to worry about", which is exactly the case.
  return {
    label: `D-${diffDays}`,
    sub: formatDate(dueDateIso),
    color: 'success'
  };
};

// Produced metres alone. An order filled off the shelf has produced nothing and
// needs nothing produced, so this read 0% for something already finished —
// `coverageOf` counts the shelf as the fulfilment it is.
const realProgressPct = order => coverageOf(order).pct;

const statusLabels = {
  pending: { label: 'Pending', color: 'warning' },
  in_progress: { label: 'In Progress', color: 'info' },
  critical: { label: 'Critical', color: 'danger' },
  done: { label: 'Completed', color: 'success' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'secondary' }
};

export const getOrdersTableColumns = ({ expanded, onToggle } = {}) => [
  {
    accessorKey: 'orderId', // 🔥 Redis object
    header: 'ID',
    cell: ({ row: { original } }) => {
      const id = original.orderId || original._id || '';
      const shortId = id ? id.slice(0, 8) + '...' : '—';

      return (
        <Link
          to={`/apps/sales-management/order-details/${id}`}
          className="text-decoration-none fw-bold fs-9"
          title={id}
        >
          {shortId}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'client',
    header: 'Customer',
    cell: ({ row: { original } }) => {
      const name = String(original.client || '').trim();
      if (!name) return <span className="text-muted">—</span>;
      // Same colour this client has on the product overview — derived from the
      // name, so you learn it once and recognise it everywhere.
      return (
        <div className="text-center">
          <Badge bg={clientBadgeColor(name)} className="fs-10 fw-bold">
            {name.toUpperCase()}
          </Badge>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    id: 'products',
    header: 'Cables',
    // Ten orders for the same customer were ten identical rows — the only way
    // to find the one you wanted was to open each. The lines were on the order
    // the whole time.
    accessorFn: row => orderProductsSearchText(row),
    cell: ({ row: { original } }) => {
      const products = orderProducts(original);
      if (!products.length) return <span className="text-muted fs-10">—</span>;

      const id = String(original.orderId);
      const open = !!expanded?.[id];

      // A count, not a list. An order can carry a dozen cables and a row that
      // grows to fit them is a row nobody can scan; the detail opens in a row
      // of its own, underneath.
      return (
        <div className="text-center">
          <Badge
            bg={open ? 'primary' : 'info'}
            className="fs-10 fw-bold"
            role={onToggle ? 'button' : undefined}
            style={{ cursor: onToggle ? 'pointer' : undefined }}
            title={
              onToggle
                ? open
                  ? 'Hide the cables'
                  : 'Show the cables and their metres'
                : products.map(p => p.label).join(', ')
            }
            onClick={onToggle ? () => onToggle(id) : undefined}
          >
            {products.length} {products.length === 1 ? 'cable' : 'cables'}
            {onToggle ? (open ? ' ▴' : ' ▾') : ''}
          </Badge>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-center' },
      cellProps: { className: 'py-2 text-center' }
    }
  },
  {
    accessorKey: 'status',
    header: 'Order Status',
    cell: ({ row: { original } }) => {
      const key = String(original.status || '').toLowerCase();
      const { label, color } = statusLabels[key] || {
        label: 'Unknown',
        color: 'dark'
      };

      return (
        <Badge bg={color} className="fs-10 rounded-pill text-center w-75 py-2">
          {label}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    id: 'productionStatus',
    header: 'Production',
    accessorFn: row => {
      // Stock counts. An order filled off the shelf needs no allocation and no
      // production, and reading only those two called it "Not allocated" for
      // ever — true, and the opposite of what it meant.
      return coverageOf(row).state;
    },
    cell: ({ row: { original } }) => {
      const cov = coverageOf(original);
      const badge = COVERAGE_BADGE[cov.state] || COVERAGE_BADGE.not_allocated;

      return (
        <Badge
          bg={badge.color}
          className="fs-10 rounded-pill text-center w-75 py-2"
        >
          {badge.label}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    id: 'urgency',
    header: 'Due',
    accessorFn: row => row?.dueDate || row?.deliveryDate || '',
    cell: ({ row: { original } }) => {
      const info = urgencyInfo(original.dueDate || original.deliveryDate);

      return (
        <div className="d-flex flex-column align-items-center">
          <Badge
            bg={info.color}
            className="fs-10 rounded-pill text-center px-3 py-2"
          >
            {info.label}
          </Badge>
          <span className="text-muted fs-10 mt-1">{info.sub}</span>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-2' }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ row: { original } }) => (
      <span className="d-block text-center">
        {formatDate(original.createdAt)}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    id: 'jobs',
    header: 'Jobs',
    accessorFn: row => getJobsCount(row),
    cell: ({ row: { original } }) => {
      const n = getJobsCount(original);
      if (!n) return <span className="text-muted">—</span>;

      return (
        <Badge
          bg="primary"
          className="fs-10 rounded-pill text-center px-3 py-2"
          title="Jobs linked via allocations"
        >
          J:{n}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'progress',
    header: 'Progress',
    cell: ({ row: { original } }) => {
      // orders have no `progress` field — use the compute-on-read fulfilment %
      // (falls back to produced/ordered).
      const progress = Number(
        original.production?.fulfillmentPct ?? realProgressPct(original)
      );

      const getProgressStyles = p => {
        if (p <= 0) return { variant: 'secondary', bgClass: 'bg-200' };
        if (p <= 40) return { variant: 'danger', bgClass: 'bg-danger-100' };
        if (p <= 80) return { variant: 'warning', bgClass: 'bg-warning-100' };
        return { variant: 'success', bgClass: 'bg-success-100' };
      };

      const { variant, bgClass } = getProgressStyles(progress);

      return (
        <div className="d-flex flex-column align-items-center w-100">
          <ProgressBar
            now={progress}
            label={`${progress}%`}
            className={`flex-1 ${bgClass}`}
            variant={variant}
            style={{
              height: '22px',
              fontSize: '14px',
              fontWeight: 'bold',
              width: '90%'
            }}
          />
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '18%' }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  }
];

/**
 * The cables an order carries, as a row of its own.
 *
 * Inline, a dozen cables either wrap the row to four lines or get truncated
 * into uselessness. Underneath, they cost nothing until somebody asks.
 */
export const OrderCablesPanel = ({ order }) => (
  <div className="px-4">
    {/* No header: the row it opens under already names the order, and
        repeating it pushed the content that matters down a line. */}
    <OrderCablesDetail order={order} />
  </div>
);

const OrdersTable = ({ renderSubRow }) => {
  return (
    <div className="border-bottom">
      <AdvanceTable
        renderSubRow={renderSubRow}
        tableProps={{
          className: 'phoenix-table border-top border-200 fs-9 text-center'
        }}
      />
      <AdvanceTableFooter pagination className="py-3" />
    </div>
  );
};

export default OrdersTable;

// The card view only needs the column set, with no expansion to drive.
export const ordersTableColumns = getOrdersTableColumns();
